import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface DotacaoOrcamentaria {
  id: number;
  codigoReduzido: string;
  dotacao: string;
  descricao?: string;
}

interface Props {
  options: DotacaoOrcamentaria[];
  value: number | undefined;
  valueText: string;
  onChange: (id: number | undefined, text: string) => void;
  disabled?: boolean;
}

const DotacaoOrcamentariaCombobox: React.FC<Props> = ({ options, value, valueText, onChange, disabled }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(valueText || '');
  }, [valueText]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useCallback(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    return options
      .filter(d => (d.descricao || '').toLowerCase().includes(q) || d.codigoReduzido.toLowerCase().includes(q))
      .slice(0, 50);
  }, [query, options]);

  return (
    <div className="mt-4" ref={ref}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Dotação Orçamentária
      </label>
      <div className="relative">
        <div
          className={`flex items-center border rounded bg-white px-3 py-2 gap-2 cursor-text ${open ? 'ring-2 ring-blue-400 border-blue-400' : 'border-gray-300'}`}
          onClick={() => !disabled && setOpen(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 shrink-0"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => !disabled && setOpen(true)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setOpen(true); } }}
            placeholder="Pesquisar dotação pela descrição..."
            className="flex-1 text-sm outline-none bg-transparent"
            disabled={disabled}
          />
          <button type="button" onClick={e => { e.stopPropagation(); if (!disabled) setOpen(true); }}
            className="px-2 py-1 bg-blue-50 border border-blue-100 text-blue-600 rounded text-xs hover:bg-blue-100">
            Buscar
          </button>
          {value && (
            <button type="button"
              onClick={e => { e.stopPropagation(); onChange(undefined, ''); setQuery(''); }}
              className="text-gray-400 hover:text-red-500 shrink-0 text-xs" disabled={disabled}>✕
            </button>
          )}
        </div>

        {open && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {filtered().length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">Nenhuma dotação encontrada</div>
            ) : (
              filtered().map(d => (
                <button key={d.id} type="button"
                  onMouseDown={() => {
                    onChange(d.id, `${d.codigoReduzido} - ${d.descricao || d.dotacao}`);
                    setQuery(`${d.codigoReduzido} — ${d.descricao || d.dotacao}`);
                    setOpen(false);
                  }}
                  className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-100 last:border-0">
                  <span className="text-xs font-mono font-bold text-blue-700 mr-2">{d.codigoReduzido}</span>
                  <span className="text-sm text-gray-700">{d.descricao || d.dotacao}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {value && <p className="text-xs text-green-700 mt-1">✅ Dotação selecionada</p>}
    </div>
  );
};

export default DotacaoOrcamentariaCombobox;
